import { convertToRailsQueryString, tryZodParse } from '@utils/api';
import {
  CampaignListAttribute,
  CampaignListEntry,
  CampaignListsResponse,
  TrendsResponse,
  campaignListsResponse,
  trendsResponse,
} from './schemas';
import { GridSortDirection } from '@mui/x-data-grid';
import { ReportType } from '../domain/report';
import { CampaignType } from '../domain/campaign';
import { rootApi } from '@components/rootApi';
import { stringify } from 'qs';
import { qsDefaults } from '@utils/utils';

type CampaignListsParameters = Partial<{
  client_ids: number[];
  page: number;
  per_page: number;
  sort_key: CampaignListAttribute;
  sort_direction: GridSortDirection;
  search_text: string;
  advertiser_ids: number[];
  campaign_type: CampaignType;
  report_type: ReportType;
  approval_status: CampaignListEntry['approvalStatus'];
  report_date_from: string;
}>;

type TrendsParams = { campaign_ids: number[] };

const pathWithBaseUrl = (path: string) => `/api/v3/campaign_lists${path}`;

const campaignListsApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    getCampaignLists: builder.query<
      CampaignListsResponse,
      CampaignListsParameters
    >({
      query: params => ({
        url: pathWithBaseUrl(`?${convertToRailsQueryString(params)}`),
      }),
      transformResponse: response =>
        tryZodParse(campaignListsResponse, response),
    }),
    getLci3WeeklyTrendCharts: builder.query<TrendsResponse, TrendsParams>({
      query: params => ({
        url: pathWithBaseUrl('/trends/weekly'),
        params,
      }),
      transformResponse: (response: TrendsResponse) =>
        trendsResponse.parse(response),
    }),

    getCpgWeeklyTrends: builder.query<TrendsResponse, TrendsParams>({
      query: params => ({
        url: pathWithBaseUrl('/cpg/trends/weekly'),
        params,
      }),
      transformResponse: (response: TrendsResponse) =>
        trendsResponse.parse(response),
    }),

    getCpgPartnerTrends: builder.query<
      TrendsResponse,
      {
        trends: {
          campaign_id: TrendsParams['campaign_ids'][number];
          channel: string;
        }[];
      }
    >({
      query: params => ({
        url: pathWithBaseUrl(
          `/cpg/trends/partner?${stringify(params, qsDefaults)}`
        ),
      }),
      transformResponse: (response: TrendsResponse) =>
        trendsResponse.parse(response),
    }),

    getRslWeeklyTrends: builder.query<TrendsResponse, TrendsParams>({
      query: params => ({
        url: pathWithBaseUrl('/rsl/trends/weekly'),
        params,
      }),
      transformResponse: (response: TrendsResponse) =>
        trendsResponse.parse(response),
    }),
  }),
});

export const {
  useGetCampaignListsQuery,
  useGetLci3WeeklyTrendChartsQuery,
  useGetCpgWeeklyTrendsQuery,
  useGetCpgPartnerTrendsQuery,
  useGetRslWeeklyTrendsQuery,
} = campaignListsApi;
